<template>
    <div :class="theme" v-if="initialLoadComplete">
        <div class="w-full mx-auto view-product">
            <div class="bg-white">
                <div class="progress">
                    <div class="w-1/3 progress-bar">
                        <div class="progress-value">1/3</div>
                    </div>
                </div>

                <div class="w-full mx-auto">
                    <section aria-labelledby="filter-heading" class="px-4 py-2">
                        <div class="relative flex items-center justify-between md:items-end">
                            <a href="" class="hidden md:flex items-center btn-primary" @click.prevent="$emit('setActiveView', 'products')">
                                Tillbaka till produkter
                            </a>
                            <a href="" class="flex md:hidden items-center btn-primary" @click.prevent="$emit('setActiveView', 'products')">
                                Tillbaka
                            </a>
                            <div class="flex items-center justify-between cursor-pointer ml-4">
                                <a v-if="cartCount > 0" href="" class="flex items-center btn-primary" @click.prevent="$emit('setActiveView', 'checkout')">
                                    <ShoppingCartIcon class="h-4 mr-1 font-normal" />
                                    Valda artiklar ({{ cartCount }})
                                </a>
                            </div>
                        </div>
                    </section>
                </div>
            </div>

            <div class="px-4 pt-5 pb-10 flex flex-col lg:flex-row gap-4">
                <div class="left grow w-full lg:w-3/4 product-info">
                    <h1>
                        {{product?.name}}
                    </h1>
                    <div
                    :class="{
                            'product-image h-[30vh] mb-4 p-4 flex': true,
                            'is-new': product.attribute_new
                        }"
                    >
                        <img ref="largeImage" :src="product?.image1_url" :alt="product.article_name" class="h-full m-auto"/>
                    </div>
                    <div class="flex justify-center mb-4 gap-4">
                        <div v-if="product.image1_url" class="product-thumbnail-image max-w-[124px] max-h-[124px] text-sm active"><img :src="product.image1_url" :alt="product.article_name" @click=toggleImage class="object-cover object-center h-full m-auto"/></div>
                        <div v-if="product.image2_url" class="product-thumbnail-image max-w-[124px] max-h-[124px] text-sm"><img :src="product.image2_url" :alt="product.article_name" @click=toggleImage class="object-cover object-center h-full m-auto"/></div>
                        <div v-if="product.image3_url" class="product-thumbnail-image max-w-[124px] max-h-[124px] text-sm"><img :src="product.image3_url" :alt="product.article_name" @click=toggleImage class="object-cover object-center h-full m-auto"/></div>
                    </div>
                    <div class="w-full mb-4 product-info-text bg-secondary-50" v-if="product.info_text">
                        <p class="block p-3 text-sm">
                            {{ product.info_text }}
                        </p>
                    </div>
                    <p v-html="product.description" class="mb-8"></p>
                    <div class="flex items-center justify-start gap-x-2.5">
                        <div
                            v-if="product.available_for_order"
                            class="flex items-center"
                        >
                            <input
                                v-model="product.quantity"
                                type="number"
                                :min="1"
                                :max="product.max_per_order > 0 ? product.max_per_order : null"
                                @input="onMaxPerOrderChange(product)"
                                class="block w-16 border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm py-[11px]"
                            />

                            <AddToCartButton
                                class="btn-primary ml-3"
                                :adding="product.loading"
                                @click="addProductToCart(product, $emit)"
                            >
                                Lägg till
                                <span class="sr-only">, {{ product.article_name }}</span>
                            </AddToCartButton>
                        </div>

                        <span
                            v-if="product.external_document_link"
                            class="cursor-pointer btn-download"
                            @click="trackFile(product, true)"
                        >
                            Ladda ner PDF
                            <span class="sr-only">, {{ product.article_name }}</span>
                        </span>
                        <span
                            v-if="product.attachment"
                            class="cursor-pointer btn-download"
                            @click="trackFile(product, false)"
                        >
                            Ladda ner PDF
                            <span class="sr-only">, {{ product.attachment.name }}</span>
                        </span>
                    </div>
                </div>
                <div class="grow w-full lg:w-1/4 related-products">
                    <h2 class="mt-8 lg:mt-0">Relaterade produkter</h2>
                    <div class="mb-8" v-for="(relatedProduct, index) in relatedProducts" v-bind:key="index">
                        <div
                            :class="{
                                'related-product-image h-[20vh] p-4 flex': true,
                                'is-new': relatedProduct.attribute_new
                            }"
                        >
                            <img v-if="relatedProduct.image1_url"
                                :src="relatedProduct.image1_url"
                                :alt="relatedProduct.article_name"
                                class="object-cover object-center h-full m-auto"
                                @click.prevent="$emit('setActiveView', 'product', {'product': relatedProduct})"
                            />
                        </div>
                        <h3><a href="" @click.prevent="$emit('setActiveView', 'product', {'product': relatedProduct})">{{ relatedProduct.name }}</a></h3>
                        <span v-html="relatedProduct.short_description"></span>
                    </div>
                </div>
            </div>
        </div>

        <!-- IMPORT SCRIPT  -->
        <component v-bind:is="'script'" :src="iframeAdjustUrl" async></component>
    </div>
</template>

<script>
    import { onMounted, ref, watch, computed } from 'vue'
    import LoadingButton                from '@/Shared/LoadingButton'
    import AddToCartButton              from '@/Shared/AddToCartButton'
    import AdobeTracking                from '@/Shared/AdobeTracking'
    import {
        Menu,
        MenuButton,
        MenuItem,
        MenuItems,
        Listbox,
        ListboxButton,
        ListboxOptions,
        ListboxOption,
    } from '@headlessui/vue'
    import { ShoppingCartIcon } from '@heroicons/vue/outline'
    import { ChevronDownIcon, SelectorIcon, CheckIcon } from '@heroicons/vue/solid'

    export default {
        components: {
            LoadingButton,
            AddToCartButton,
            Menu,
            MenuButton,
            MenuItem,
            MenuItems,
            Listbox,
            ListboxButton,
            ListboxOptions,
            ListboxOption,
            ChevronDownIcon,
            SelectorIcon,
            CheckIcon,
            ShoppingCartIcon,
        },
        props: {
            parentCategory: {
                type: [Object, null],
                default: null,
            },
            childCategory: {
                type: [Object, null],
                default: null,
            },
            productId: {
                type: [Number, null],
                default: null,
            },
            cart: {
                type: Object,
                required: false,
            },
            sharableCartId: {
                type: String,
                required: false,
            },
        },
        watch: {
            productId: function(newValue){
                this.loadProduct(false, newValue)
            }
        },
        setup(props) {
            const theme = ref(props.parentCategory.color_scheme)
            const cart = ref(props.cart)
            const sharableCartId = ref(props.sharableCartId)
            const childCategory = ref(null)
            const iframeAdjustUrl = ref(process.env.MIX_API_URL + '/iframe-adjust.js')
            const loading = ref(false)
            const initialLoadComplete = ref(false)
            const {track} = AdobeTracking();
            const product = ref(null)
            const relatedProducts = ref([])
            const largeImage = ref(null)

            const refreshCart = () => {
                if (localStorage.getItem('pf-cart')) {
                    cart.value = JSON.parse(localStorage.getItem('pf-cart'))
                } else {
                    cart.value = []
                }
            }

            watch(() => props.cart, (newCart) => {
                cart.value = newCart
            }, { immediate: true })

            watch(() => props.sharableCartId, (newSharableCartId) => {
                sharableCartId.value = newSharableCartId
            }, { immediate: true })

            const cartCount = computed(() => cart.value ? Object.values(cart.value).reduce((acc, item) => acc + item.quantity, 0) : 0)

            const addProductToCart = (product, emit) => {
                product.loading = true

                window.dispatchEvent(new CustomEvent('pfizershop', {
                    detail: {
                        action: 'add-to-cart',
                        product: {
                            id: product.id,
                            name: product.name,
                        },
                        quantity: product.quantity,
                    }
                }));

                track('pfMaterialOrder',
                    {       
                        'productInfo': `;${product.name};${product.quantity}`, //value format is ';{product name};{quantity}'
                        'orderAction':'cart addition', // actions are: cart addition, cart removal, checkout, completed
                        'orderID': '0', //Order ID, mandatory for completed action
                        'userID': '0', //User ID, Customer ID, GRV ID
                        'userType': 'generic', // GRV, PAC, DIN, generic			
                        'materialOrderPageLoad': 'false' //hardcode to false for clicks
                    }
                );

                fetch(process.env.MIX_API_URL+'/cart/add', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        product_id: product.id,
                        quantity: product.quantity,
                        cart: cart.value,
                        sharable_cart_id: sharableCartId.value,
                    }),
                }).then((response) => response.json()).then((data) => {
                    if (data.status === 'success') {
                        cart.value = data.cart
                        emit('cartUpdated', cart.value);
                        // localStorage.setItem('pf-cart', JSON.stringify(cart.value));
                    }
                }).finally(() => {
                    setTimeout(() => {
                        product.loading = false
                    }, 4000);
                })
            }

            const loadProduct = (initialPageLoad, productId) => {
                loading.value = true

                let url = process.env.MIX_API_URL+'/api/shop/product/'+productId;

                if (props.parentCategory?.id){
                    url += '/'+props.parentCategory.id
                }

                if (props.childCategory?.id){
                    url += '/'+props.childCategory.id
                }

                fetch(url, {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' },
                }).then((response) => response.json()).then((data) => {
                    product.value = data.product
                    relatedProducts.value = data.relatedProducts
                }).finally(() => {
                    loading.value = false
                    window.dispatchEvent(new Event('resize'))

                    if (initialPageLoad === true) {
                        initialLoadComplete.value = true
                    }
                })
            }

            const onMaxPerOrderChange = (product) => {
                if (product.max_per_order > 0 && product.quantity > product.max_per_order) {
                    product.quantity = product.max_per_order
                }
                if (product.quantity < 1) {
                    product.quantity = 1
                }
            }

            const trackFile = async (product, externalLink = false) => {
                if (product) {
                    window.dispatchEvent(new CustomEvent('pfizershop', {
                        detail: {
                            action: 'download-pdf',
                            product: {
                                id: product.id,
                                name: product.name,
                            }
                        }
                    }));

                    track('pfMaterialDownload', {'productName': product.name});

                    await fetch(process.env.MIX_API_URL+'/api/shop/track-file', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            product_id: product.id,
                            product_name: product.name,
                            category_id: props.parentCategory.id,
                            sub_category_id: childCategory.value?.id ?? null,
                        })
                    }).then((response) => response.json()).then((data) => {})

                    if (externalLink) {
                        window.open(product.external_document_link, '_blank')
                    } else {
                        window.open(product.attachment.url, '_blank')
                    }
                }
            }

            const toggleImage = (e) => {
                document.querySelectorAll('.product-thumbnail-image').forEach((el) => {
                    el.classList.remove('active')
                });
                
                e.srcElement.parentElement.classList.add('active');
                largeImage.value.src = e.srcElement.attributes.src.value
            }

            onMounted(() => {
                loadProduct(true, props.productId)
                refreshCart()
            })

            return {
                iframeAdjustUrl,
                theme,
                initialLoadComplete,
                largeImage,
                toggleImage,
                cartCount,
                addProductToCart,
                loadProduct,
                onMaxPerOrderChange,
                trackFile,
                product,
                relatedProducts
            }
        }
    }
</script>

<style>
    .product-content--purple a,
    .read-more-btn--purple {
        color: rgb(141, 49, 86);
    }

    .product-content--blue a,
    .read-more-btn--blue {
        color: #097eaf;
    }

    .read-more-btn {
        display: block;
        margin-top: 16px;
        text-decoration: underline;
        cursor: pointer;
    }
</style>